// frontend/src/features/inventory/lookup/StockTransferForm.tsx  (فایل جدید)
// AM-036 فاز ۲: فرم مستقل انتقال بین دو انبار → POST /api/v1/inventory/stock-transactions/transfer/
// (رفع شکست فرم قدیمی که transfer_out/transfer_in را به create می‌فرستاد.)
//
// اتصال: داخل StockPage به‌صورت <StockTransferForm materials=... warehouses=... onDone=... />
// نکته: تا تولید خودکار کلاینت orval برای اکشن transfer (چرخهٔ RM-12، دامنهٔ mononoor2)،
// از axiosInstance مستقیم استفاده می‌شود؛ پس از تولید، فقط همین یک فراخوانی جایگزین می‌شود.

import { useCallback, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { axiosInstance } from "@/api/client";
import { applyLotToForm, extractApiErrorMessage, remainingInWarehouse } from "./lotScan";
import type { LotLookupResponse } from "./lotLookupTypes";
import { useLotLookup } from "./useLotLookup";

export interface TransferWarehouseOption {
  id: string;
  name: string;
}

interface StockTransferFormProps {
  warehouses: TransferWarehouseOption[];
  /** شمارندهٔ اسکن‌شده را به پالت تبدیل می‌کند؛ ScannableInput پروژه از بیرون تزریق می‌شود. */
  renderScanner: (onScanned: (code: string) => void | Promise<void>, disabled: boolean) => ReactNode;
  onDone: () => void;
}

const TRANSFER_URL = "/api/v1/inventory/stock-transactions/transfer/";

export function StockTransferForm({ warehouses, renderScanner, onDone }: StockTransferFormProps) {
  const { lookup, isScanning } = useLotLookup();
  const [lot, setLot] = useState<LotLookupResponse | null>(null);
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [referenceDoc, setReferenceDoc] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleScan = useCallback(
    async (code: string) => {
      setError(null);
      const result = await lookup(code);
      if (result.status === "cancelled") return;
      if (result.status === "error") {
        setLot(null);
        setError(result.message);
        return;
      }
      const outcome = applyLotToForm(result.lot, "transfer_out");
      if (!outcome.ok) {
        setLot(null);
        setError(outcome.message);
        return;
      }
      setLot(result.lot);
      setFromWarehouseId(outcome.patch.warehouseId);
      setQuantity("");
    },
    [lookup],
  );

  const maxQuantity = lot && fromWarehouseId ? remainingInWarehouse(lot, fromWarehouseId) : null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!lot) return setError("ابتدا پالت را اسکن کنید.");
    if (!fromWarehouseId) return setError("انبار مبدأ را انتخاب کنید.");
    if (!toWarehouseId) return setError("انبار مقصد را انتخاب کنید.");
    if (fromWarehouseId === toWarehouseId) return setError("انبار مقصد نباید با انبار مبدأ یکی باشد.");
    if (maxQuantity === null) return setError("این پالت در انبار مبدأ موجودی ندارد.");
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) return setError("مقدار باید عددی مثبت باشد.");
    if (qty > Number(maxQuantity)) return setError(`حداکثر مقدار قابل انتقال ${maxQuantity} است.`);

    setSubmitting(true);
    try {
      await axiosInstance.post(TRANSFER_URL, {
        material: lot.material_family_id,
        from_warehouse: fromWarehouseId,
        to_warehouse: toWarehouseId,
        quantity,
        lot_id: lot.id,
        reference_doc: referenceDoc,
      });
      onDone();
    } catch (raw) {
      const response = (raw as { response?: { data?: unknown } }).response;
      setError(extractApiErrorMessage(response?.data, "ثبت انتقال با خطا مواجه شد."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} dir="rtl" aria-label="فرم انتقال بین انبارها">
      <div>{renderScanner(handleScan, isScanning || submitting)}</div>

      {lot && (
        <p>
          پالت <strong>{lot.lot_number}</strong> — {lot.material_name ?? lot.material_code}
          {lot.qc_status_label ? ` (وضعیت کیفی: ${lot.qc_status_label})` : ""}
        </p>
      )}

      <label htmlFor="transfer-from">انبار مبدأ</label>
      <select
        id="transfer-from"
        value={fromWarehouseId}
        onChange={(e) => setFromWarehouseId(e.target.value)}
        disabled={!lot}
      >
        <option value="">انتخاب کنید</option>
        {(lot?.stock ?? []).map((s) => (
          <option key={s.warehouse_id} value={s.warehouse_id}>
            {s.warehouse_name ?? s.warehouse_id} — موجودی {s.qty}
          </option>
        ))}
      </select>

      <label htmlFor="transfer-to">انبار مقصد</label>
      <select id="transfer-to" value={toWarehouseId} onChange={(e) => setToWarehouseId(e.target.value)}>
        <option value="">انتخاب کنید</option>
        {warehouses
          .filter((w) => w.id !== fromWarehouseId)
          .map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
      </select>

      <label htmlFor="transfer-qty">مقدار{maxQuantity ? ` (حداکثر ${maxQuantity})` : ""}</label>
      <input
        id="transfer-qty"
        inputMode="decimal"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        disabled={!lot}
      />

      <label htmlFor="transfer-ref">سند مرجع</label>
      <input id="transfer-ref" value={referenceDoc} onChange={(e) => setReferenceDoc(e.target.value)} />

      <div role="alert" aria-live="polite">
        {error}
      </div>
      <button type="submit" disabled={submitting || isScanning || !lot}>
        {submitting ? "در حال ثبت…" : "ثبت انتقال"}
      </button>
    </form>
  );
}
